import Avatar from '@app/src/components/Ui/Avatar'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import useOrganizationSettings from '@app/src/hooks/useOrganizationSettings'
import { isAdminRole } from '@app/src/utils/rolePermissions'
import {
  ArrowForwardRounded,
  DoNotDisturbOnOutlined,
  InfoOutlined,
  LanguageOutlined,
  PictureAsPdfOutlined,
} from '@mui/icons-material'
import { Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import OrganizationSettingSwitch from './OrganizationSettingSwitch'

interface OutcomeStepProps {
  icon: React.ReactNode
  label: string
  muted?: boolean
}

const OutcomeStep: React.FC<OutcomeStepProps> = ({ icon, label, muted }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <Avatar
      sx={({ palette }) => ({
        bgcolor: muted ? palette.grey[300] : palette.secondary.main,
        color: muted ? palette.text.disabled : undefined,
      })}
    >
      {icon}
    </Avatar>
    <Typography variant="body1" color={muted ? 'text.secondary' : 'text.primary'}>
      {label}
    </Typography>
  </Stack>
)

const WebPageCaptureSettings: React.FC = () => {
  const { formatMessage } = useIntl()
  const { role } = useAuthentication().scope
  const { settings } = useOrganizationSettings()
  const isAdmin = isAdminRole(role)

  // An API without the setting cannot save it, so the card stays hidden until it does.
  if (settings?.aiCaptureWebPagesAsFiles === undefined) return null

  const isOn = settings.aiCaptureWebPagesAsFiles
  const stateIntlKey = isOn ? 'on' : 'off'

  return (
    <Stack bgcolor="background.paper" borderRadius={2} p={4} spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1">{formatMessage({ id: 'aiScreening.webPageCapture.title' })}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatMessage({ id: 'aiScreening.webPageCapture.description' })}
          </Typography>
        </Stack>
        <OrganizationSettingSwitch
          settingKey="aiCaptureWebPagesAsFiles"
          ariaLabel={formatMessage({ id: 'aiScreening.webPageCapture.title' })}
        />
      </Stack>

      <Stack bgcolor="grey.100" borderRadius={2} p={2.5} spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <OutcomeStep
            icon={<LanguageOutlined />}
            label={formatMessage({ id: 'aiScreening.webPageCapture.webPageFound' })}
          />
          <ArrowForwardRounded color="action" sx={{ transform: { xs: 'rotate(90deg)', sm: 'none' }, ml: { xs: 1 } }} />
          <OutcomeStep
            icon={isOn ? <PictureAsPdfOutlined /> : <DoNotDisturbOnOutlined />}
            label={formatMessage({ id: `aiScreening.webPageCapture.outcome.${stateIntlKey}` })}
            muted={!isOn}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: `aiScreening.webPageCapture.outcomeDescription.${stateIntlKey}` })}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="flex-start" color="text.secondary">
        <InfoOutlined fontSize="small" />
        <Typography variant="body2">
          {formatMessage({ id: 'aiScreening.webPageCapture.appliesToNewScreenings' })}
          {!isAdmin && ` ${formatMessage({ id: 'aiScreening.acceptance.adminOnly' })}`}
        </Typography>
      </Stack>
    </Stack>
  )
}

export default WebPageCaptureSettings
